/**
 * Structured answer values and safe parsing/normalization of student input.
 *
 * Grading always happens on these structured values, never on raw strings, so
 * "6", "x=6", and "x = 6" all reduce to the same NumberAnswer and compare
 * equal. Parsing is a small hand-written tokenizer - there is no eval and no
 * dynamic expression execution anywhere. Unrecognized input is rejected, never
 * guessed.
 */

import { Rational, rat } from "./rational";

export type InequalityOp = "<" | "<=" | ">" | ">=";

/** A single inequality bound on the variable, e.g. x < 4 or x >= -2. */
export interface SimpleInequality {
  readonly kind: "inequality";
  readonly op: InequalityOp;
  readonly value: Rational;
}

/**
 * A compound solution over the variable. AND (intersection, a bounded
 * interval) or OR (union of two rays). Bounds carry inclusivity.
 */
export interface CompoundInequality {
  readonly kind: "compound";
  readonly connector: "and" | "or";
  readonly lower: { readonly value: Rational; readonly inclusive: boolean };
  readonly upper: { readonly value: Rational; readonly inclusive: boolean };
}

export interface NumberAnswer {
  readonly kind: "number";
  readonly value: Rational;
}

export interface SpecialAnswer {
  readonly kind: "special";
  readonly value: "no-solution" | "infinitely-many";
}

export interface BooleanAnswer {
  readonly kind: "boolean";
  readonly value: boolean;
}

export interface ChoiceAnswer {
  readonly kind: "choice";
  readonly value: string;
}

/** An unordered set of rational solutions, e.g. absolute-value equations. */
export interface SetAnswer {
  readonly kind: "set";
  readonly values: readonly Rational[];
}

export type AnswerValue =
  | NumberAnswer
  | SimpleInequality
  | CompoundInequality
  | SpecialAnswer
  | BooleanAnswer
  | ChoiceAnswer
  | SetAnswer;

/** Build a canonical SetAnswer: deduped, sorted ascending. */
export function makeSetAnswer(values: readonly Rational[]): SetAnswer {
  const uniq: Rational[] = [];
  for (const v of values) {
    if (!uniq.some((u) => u.equals(v))) uniq.push(v);
  }
  uniq.sort((a, b) => a.compare(b));
  return { kind: "set", values: uniq };
}

export interface ParseSuccess {
  readonly ok: true;
  readonly value: AnswerValue;
}
export interface ParseFailure {
  readonly ok: false;
  readonly reason: string;
}
export type ParseResult = ParseSuccess | ParseFailure;

const ok = (value: AnswerValue): ParseResult => ({ ok: true, value });
const fail = (reason: string): ParseResult => ({ ok: false, reason });

/** Normalize symbol variants and whitespace before parsing. */
export function normalizeInput(raw: string): string {
  return raw
    .trim()
    .replace(/\u2264/g, "<=") // ≤
    .replace(/\u2265/g, ">=") // ≥
    .replace(/\u2212/g, "-") // unicode minus
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const NO_SOLUTION_SYNONYMS = new Set([
  "no solution",
  "no solutions",
  "none",
  "no sol",
  "empty",
  "no real solution",
  "no real solutions",
]);

const INFINITE_SYNONYMS = new Set([
  "infinitely many",
  "infinitely many solutions",
  "infinite",
  "infinite solutions",
  "all real numbers",
  "all reals",
  "infinitely many solution",
]);

const YES_SYNONYMS = new Set(["yes", "y", "true", "function", "is a function"]);
const NO_SYNONYMS = new Set(["no", "n", "false", "not a function", "not"]);

/** Parse a rational literal: "6", "-4", "3/5", "-3/5". Returns null if not one. */
function parseRationalLiteral(s: string): Rational | null {
  const cleaned = s.replace(/\s+/g, "");
  // Integer
  if (/^[+-]?\d+$/.test(cleaned)) {
    return rat(BigInt(cleaned));
  }
  // Fraction a/b
  const m = /^([+-]?\d+)\/([+-]?\d+)$/.exec(cleaned);
  if (m) {
    const num = BigInt(m[1] as string);
    const den = BigInt(m[2] as string);
    if (den === 0n) return null;
    return rat(num, den);
  }
  return null;
}

/** Strip a leading "x =" / "x=" prefix if present. */
function stripVariablePrefix(s: string): string {
  const m = /^[a-z]\s*=\s*(.*)$/.exec(s);
  return m ? (m[1] as string) : s;
}

const OP_TOKENS: readonly InequalityOp[] = [">=", "<=", ">", "<"];

/**
 * Parse a general answer string into a structured AnswerValue.
 *
 * Order of attempts: special phrases, boolean phrases, compound inequality,
 * simple inequality, number/fraction. The caller usually knows the expected
 * answer type and can restrict acceptance, but this general parser is handy
 * for tests and lenient input.
 */
export function parseAnswer(raw: string): ParseResult {
  const s = normalizeInput(raw);
  if (s === "") return fail("empty input");

  if (NO_SOLUTION_SYNONYMS.has(s)) {
    return ok({ kind: "special", value: "no-solution" });
  }
  if (INFINITE_SYNONYMS.has(s)) {
    return ok({ kind: "special", value: "infinitely-many" });
  }
  if (YES_SYNONYMS.has(s)) return ok({ kind: "boolean", value: true });
  if (NO_SYNONYMS.has(s)) return ok({ kind: "boolean", value: false });

  const compound = tryParseCompound(s);
  if (compound) return compound;

  const simple = tryParseSimpleInequality(s);
  if (simple) return simple;

  // Solution set: two or more numbers separated by "," or " or ".
  const setResult = tryParseSet(s);
  if (setResult) return setResult;

  const stripped = stripVariablePrefix(s);
  const r = parseRationalLiteral(stripped);
  if (r) return ok({ kind: "number", value: r });

  return fail(`could not parse "${raw}"`);
}

/** Parse "a, b" or "x=a or x=b" or "a or b" into a solution set. */
function tryParseSet(s: string): ParseResult | null {
  // Split on comma or the word "or"; only accept if >= 2 numeric parts.
  const parts = s.split(/\s*,\s*|\s+or\s+/).filter((p) => p.trim() !== "");
  if (parts.length < 2) return null;
  const values: Rational[] = [];
  for (const part of parts) {
    const r = parseRationalLiteral(stripVariablePrefix(part.trim()));
    if (!r) return null;
    values.push(r);
  }
  return ok(makeSetAnswer(values));
}

function tryParseSimpleInequality(s: string): ParseResult | null {
  // Forms: "x < 4", "x>=-2", "4 > x" (variable on either side).
  for (const op of OP_TOKENS) {
    const idx = s.indexOf(op);
    if (idx === -1) continue;
    const lhs = s.slice(0, idx).trim();
    const rhs = s.slice(idx + op.length).trim();

    // variable OP value
    if (/^[a-z]$/.test(lhs)) {
      const v = parseRationalLiteral(rhs);
      if (v) return ok({ kind: "inequality", op, value: v });
    }
    // value OP variable  ->  flip to variable form
    if (/^[a-z]$/.test(rhs)) {
      const v = parseRationalLiteral(lhs);
      if (v) return ok({ kind: "inequality", op: flipOp(op), value: v });
    }
    return null; // an op was present but neither side was a bare variable
  }
  return null;
}

function flipOp(op: InequalityOp): InequalityOp {
  switch (op) {
    case "<":
      return ">";
    case ">":
      return "<";
    case "<=":
      return ">=";
    case ">=":
      return "<=";
  }
}

function tryParseCompound(s: string): ParseResult | null {
  // AND written as a triple bound: "a < x <= b" (any op pair, variable in middle)
  const triple =
    /^([+-]?\d+(?:\/[+-]?\d+)?)\s*(<=|<)\s*([a-z])\s*(<=|<)\s*([+-]?\d+(?:\/[+-]?\d+)?)$/.exec(
      s,
    );
  if (triple) {
    const lo = parseRationalLiteral(triple[1] as string);
    const hi = parseRationalLiteral(triple[5] as string);
    if (!lo || !hi) return null;
    const loInc = triple[2] === "<=";
    const hiInc = triple[4] === "<=";
    return ok({
      kind: "compound",
      connector: "and",
      lower: { value: lo, inclusive: loInc },
      upper: { value: hi, inclusive: hiInc },
    });
  }

  // OR written with a keyword: "x < a or x >= b"
  if (s.includes(" or ")) {
    const [left, right] = s.split(" or ");
    const l = tryParseSimpleInequality((left as string).trim());
    const r = tryParseSimpleInequality((right as string).trim());
    if (l && l.ok && r && r.ok) {
      const li = l.value as SimpleInequality;
      const ri = r.value as SimpleInequality;
      // Normalize into lower/upper rays: a "less-than" ray is the lower piece.
      const lessOps: InequalityOp[] = ["<", "<="];
      const lower = lessOps.includes(li.op) ? li : ri;
      const upper = lessOps.includes(li.op) ? ri : li;
      return ok({
        kind: "compound",
        connector: "or",
        lower: { value: lower.value, inclusive: lower.op === "<=" },
        upper: { value: upper.value, inclusive: upper.op === ">=" },
      });
    }
  }

  return null;
}

/** Structural equality of two answer values, used for grading. */
export function equalsAnswer(a: AnswerValue, b: AnswerValue): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "number":
      return a.value.equals((b as NumberAnswer).value);
    case "special":
      return a.value === (b as SpecialAnswer).value;
    case "boolean":
      return a.value === (b as BooleanAnswer).value;
    case "choice":
      return a.value === (b as ChoiceAnswer).value;
    case "inequality": {
      const o = b as SimpleInequality;
      return a.op === o.op && a.value.equals(o.value);
    }
    case "compound": {
      const o = b as CompoundInequality;
      return (
        a.connector === o.connector &&
        a.lower.value.equals(o.lower.value) &&
        a.lower.inclusive === o.lower.inclusive &&
        a.upper.value.equals(o.upper.value) &&
        a.upper.inclusive === o.upper.inclusive
      );
    }
    case "set": {
      const o = b as SetAnswer;
      if (a.values.length !== o.values.length) return false;
      // Both are canonicalized (sorted, deduped) by makeSetAnswer.
      return a.values.every((v, i) => v.equals(o.values[i] as Rational));
    }
  }
}

/**
 * Grade student input against an expected structured answer. Parses leniently,
 * then compares structurally. Restrict acceptance to the expected kind so, for
 * example, a bare number is not silently accepted for an inequality question.
 */
export function gradeAnswer(raw: string, expected: AnswerValue): boolean {
  const parsed = parseAnswer(raw);
  if (!parsed.ok) return false;
  return equalsAnswer(parsed.value, expected);
}

/**
 * Grade a submission against a question-style expected answer. For "choice"
 * answers the submission is the selected choice id and is compared directly;
 * for all other kinds the submission is free text parsed by gradeAnswer.
 */
export function gradeSubmission(
  submission: string,
  expected: AnswerValue,
): boolean {
  if (expected.kind === "choice") {
    return submission === expected.value;
  }
  // A single-element set may be entered as a bare number (e.g. range = {5}).
  if (expected.kind === "set" && expected.values.length === 1) {
    const parsed = parseAnswer(submission);
    if (parsed.ok && parsed.value.kind === "number") {
      return parsed.value.value.equals(expected.values[0] as Rational);
    }
  }
  return gradeAnswer(submission, expected);
}
